import { MapPin, Plus } from 'lucide-react';
import {
  useState,
  type CSSProperties,
  type FormEvent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import type {
  BoardFaceList,
  BoardMap,
  CardLocationPut,
  LabelColour,
  MapCardRef,
  MapPin as BoardMapPin,
} from '@molt/shared';
import { Button } from '@/components/Button';
import { TextField } from '@/components/TextField';
import { labelColourDisplayName, labelSwatch } from '@/components/palette';
import {
  mapPinAppearanceLabel,
  mapPinPosition,
  normaliseMapCoordinateInput,
} from '@/lib/board-map';
import { cardDisplayTitle } from '@/lib/card-title';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

export interface BoardMapCreateIntent {
  readonly title: string;
  readonly listId: string;
  readonly location: CardLocationPut;
}

export interface BoardMapOpenCardIntent {
  readonly card: MapCardRef;
}

export interface BoardMapViewProps {
  readonly map: BoardMap;
  readonly allLists: readonly BoardFaceList[];
  readonly readonly?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly onCreateCard?: ((intent: BoardMapCreateIntent) => void) | undefined;
  readonly onOpenCard?: ((intent: BoardMapOpenCardIntent) => void) | undefined;
  readonly onQuickEditCard?: ((intent: BoardMapOpenCardIntent) => void) | undefined;
  readonly onRenameCard?: ((cardId: string, title: string) => void) | undefined;
}

interface CreateDraft {
  readonly title: string;
  readonly listId: string;
  readonly text: string;
  readonly lat: string;
  readonly lng: string;
}

interface RenameDraft {
  readonly cardId: string;
  readonly title: string;
}

export function BoardMapView({
  map,
  allLists,
  readonly = false,
  busy = false,
  onCreateCard,
  onOpenCard,
  onQuickEditCard,
  onRenameCard,
}: BoardMapViewProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [createDraft, setCreateDraft] = useState<CreateDraft | null>(null);
  const [renameDraft, setRenameDraft] = useState<RenameDraft | null>(null);
  /* ⚠ A KEY, not a translated sentence. `ProfilePanel`'s refusal flag is the
     precedent: this state outlives the render that set it, so holding the
     English of the moment would leave a stale sentence on screen the first
     time the catalogue query resolves under it. */
  const [localErrorKey, setLocalErrorKey] = useState<string | null>(null);
  const selected = map.pins[selectedIndex] ?? null;
  const canEdit = !readonly && map.board.closed === false;
  const createDisabledReason = map.board.closed
    ? t('boardMap.createDisabled.closed')
    : readonly
      ? t('boardMap.createDisabled.readonly')
      : allLists.length === 0
        ? t('boardMap.createDisabled.noLists')
        : undefined;
  const pinCount = map.pins.reduce((count, pin) => count + pin.cards.length, 0);

  function openCreateDraft(event?: MouseEvent<HTMLElement>): void {
    if (!canEdit || allLists.length === 0) return;
    const coordinates = coordinatesFromEvent(event);
    setCreateDraft({
      title: '',
      listId: allLists[0]!.id,
      text: '',
      lat: String(coordinates.lat),
      lng: String(coordinates.lng),
    });
    setLocalErrorKey(null);
  }

  function submitCreate(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (createDraft === null) return;
    const title = createDraft.title.trim();
    if (title === '') {
      setLocalErrorKey('boardMap.titleRequired');
      return;
    }
    const location = locationFromDraft(createDraft);
    if (location === null) {
      setLocalErrorKey('boardMap.coordinatesInvalid');
      return;
    }
    onCreateCard?.({ title, listId: createDraft.listId, location });
    setCreateDraft(null);
    setLocalErrorKey(null);
  }

  function submitRename(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (renameDraft === null) return;
    const title = renameDraft.title.trim();
    if (title !== '') onRenameCard?.(renameDraft.cardId, title);
    setRenameDraft(null);
  }

  return (
    <section
      id="board-view-content"
      aria-label={t('boardMap.viewLabel')}
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-surface px-3 pb-3 text-ink"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-ink">{t('boardMap.heading')}</h2>
          {/* ⚠ TWO independent counts in one sentence, and the catalogue's
              plural arm carries exactly ONE `count`. So the two quantities are
              their own plural keys and the sentence is a third key that slots
              them — which is also the only shape a locale with a third plural
              category can express. The alternative (four sentence keys, one per
              combination) multiplies with every count added. */}
          <p className="text-card text-ink-muted">
            {t('boardMap.summary', {
              cards: t('boardMap.summaryCards', { count: pinCount }, pinCount),
              pins: t(
                'boardMap.summaryPins',
                { count: map.pins.length },
                map.pins.length,
              ),
              board: map.board.title,
            })}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant={createDraft === null ? 'secondary' : 'toggle'}
          pressed={createDraft !== null}
          iconStart={<Plus aria-hidden="true" className="size-4" />}
          disabled={!canEdit || allLists.length === 0 || busy}
          disabledReason={createDisabledReason}
          onClick={() => openCreateDraft()}
        >
          {t('boardMap.addCard')}
        </Button>
      </div>

      {map.excludedCardCount > 0 ? (
        <div
          role="status"
          className="rounded-card border border-border bg-surface-raised px-3 py-2 text-card"
        >
          <p className="font-medium text-ink">
            {t(
              'boardMap.excludedTitle',
              { count: map.excludedCardCount },
              map.excludedCardCount,
            )}
          </p>
          <p className="text-ink-muted">
            {t(
              'boardMap.excludedBody',
              { count: map.excludedCardCount },
              map.excludedCardCount,
            )}
          </p>
        </div>
      ) : null}

      {createDraft === null ? null : (
        <form
          aria-label={t('boardMap.createFormLabel')}
          className="grid gap-2 rounded-card border border-border-strong bg-panel p-3 shadow-card md:grid-cols-[minmax(12rem,1fr)_minmax(10rem,14rem)_repeat(2,minmax(8rem,10rem))_auto]"
          onSubmit={submitCreate}
        >
          <TextField
            label={t('boardMap.cardTitle')}
            value={createDraft.title}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setCreateDraft((current) =>
                current === null ? null : { ...current, title: value },
              );
            }}
          />
          <label className="flex min-w-0 flex-col gap-1 text-card font-medium text-ink">
            {t('boardMap.listLabel')}
            <select
              className="h-9 rounded-card border border-border-strong bg-surface px-2 text-card text-ink"
              value={createDraft.listId}
              disabled={busy}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setCreateDraft((current) =>
                  current === null ? null : { ...current, listId: value },
                );
              }}
            >
              {allLists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.title}
                </option>
              ))}
            </select>
          </label>
          <TextField
            label={t('boardMap.latitude')}
            type="number"
            step="0.0000001"
            min={-90}
            max={90}
            value={createDraft.lat}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setCreateDraft((current) =>
                current === null ? null : { ...current, lat: value },
              );
            }}
          />
          <TextField
            label={t('boardMap.longitude')}
            type="number"
            step="0.0000001"
            min={-180}
            max={180}
            value={createDraft.lng}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setCreateDraft((current) =>
                current === null ? null : { ...current, lng: value },
              );
            }}
          />
          <div className="flex items-end gap-2">
            <Button type="submit" size="sm" variant="primary" busy={busy}>
              {t('boardMap.create')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="quiet"
              onClick={() => setCreateDraft(null)}
            >
              {t('boardMap.cancel')}
            </Button>
          </div>
          {localErrorKey === null ? null : (
            <p className="text-card text-bad md:col-span-5">{t(localErrorKey)}</p>
          )}
        </form>
      )}

      <div className="grid min-h-[34rem] gap-3 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div
          role="application"
          aria-label={t('boardMap.canvas')}
          className="relative min-h-[28rem] overflow-hidden rounded-card border border-border-strong bg-panel shadow-card"
          onDoubleClick={openCreateDraft}
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem]"
          />
          <div
            aria-hidden="true"
            className="absolute left-3 top-3 text-xs text-ink-muted"
          >
            90 N
          </div>
          <div
            aria-hidden="true"
            className="absolute bottom-3 right-3 text-xs text-ink-muted"
          >
            90 S / 180 E
          </div>
          <div
            role="group"
            aria-label={t('boardMap.pinsGroup')}
            className="absolute inset-0"
          >
            {map.pins.map((pin, index) => (
              <MapPinButton
                key={`${pin.location.lat}:${pin.location.lng}`}
                pin={pin}
                selected={selectedIndex === index}
                onSelect={() => setSelectedIndex(index)}
              />
            ))}
          </div>
          {map.pins.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="max-w-sm rounded-card border border-border bg-surface-raised p-4 text-center shadow-card">
                <h3 className="font-semibold text-ink">{t('boardMap.emptyTitle')}</h3>
                <p className="mt-1 text-card text-ink-muted">{t('boardMap.emptyBody')}</p>
              </div>
            </div>
          ) : null}
        </div>

        <aside
          aria-label={t('boardMap.asideLabel')}
          className="flex min-h-0 flex-col gap-3 rounded-card border border-border-strong bg-panel p-3 shadow-card"
        >
          {selected === null ? (
            <p className="text-card text-ink-muted">{t('boardMap.selectPin')}</p>
          ) : (
            <>
              <div>
                <h3 className="font-semibold text-ink">
                  {selected.location.text ??
                    t('boardMap.coordinates', {
                      lat: selected.location.lat,
                      lng: selected.location.lng,
                    })}
                </h3>
                <p className="text-card text-ink-muted">
                  {t(
                    'boardMap.cardsAtLocation',
                    { count: selected.cards.length },
                    selected.cards.length,
                  )}
                </p>
              </div>
              <ol className="flex min-h-0 flex-col gap-2 overflow-y-auto">
                {selected.cards.map((card) => (
                  <li
                    key={card.id}
                    className="rounded-card border border-border bg-surface px-3 py-2"
                  >
                    {renameDraft?.cardId === card.id ? (
                      <form
                        aria-label={t('boardMap.quickEditForm', {
                          title: cardDisplayTitle(t, card.title),
                        })}
                        className="flex flex-col gap-2"
                        onSubmit={submitRename}
                      >
                        <TextField
                          label={t('boardMap.renameCardTitle')}
                          value={renameDraft.title}
                          onChange={(event) =>
                            setRenameDraft({
                              cardId: card.id,
                              title: event.currentTarget.value,
                            })
                          }
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button type="submit" size="sm" variant="primary" busy={busy}>
                            {t('boardMap.save')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="quiet"
                            onClick={() => setRenameDraft(null)}
                          >
                            {t('boardMap.renameCancel')}
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-ink">
                            {cardDisplayTitle(t, card.title)}
                          </p>
                          <p className="text-xs text-ink-muted">
                            {t('boardMap.pinAppearanceLine', {
                              appearance: mapPinAppearanceLabel(t, {
                                ...selected,
                                cards: [card],
                              }),
                            })}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => onOpenCard?.({ card })}
                          >
                            {t('boardMap.openCard', {
                              title: cardDisplayTitle(t, card.title),
                            })}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="quiet"
                            disabled={!canEdit}
                            disabledReason={
                              canEdit ? undefined : t('boardMap.quickEditDisabled')
                            }
                            onClick={() => {
                              onQuickEditCard?.({ card });
                              setRenameDraft({
                                cardId: card.id,
                                title: card.title.trim(),
                              });
                            }}
                          >
                            {t('boardMap.quickEditCard', {
                              title: cardDisplayTitle(t, card.title),
                            })}
                          </Button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

function MapPinButton({
  pin,
  selected,
  onSelect,
}: {
  readonly pin: BoardMapPin;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const position = mapPinPosition(pin.location);
  const { style, label, className } = pinStyle(t, pin);
  const title =
    pin.location.text ??
    t('boardMap.coordinates', { lat: pin.location.lat, lng: pin.location.lng });
  const names = pin.cards
    .slice(0, 3)
    .map((card) => cardDisplayTitle(t, card.title))
    .join(', ');
  const overflow = Math.max(pin.cards.length - 3, 0);
  /* ⚠ TWO whole sentences rather than one with a conditional ` and N more`
     fragment spliced into it. The fragment shape needs a LEADING space inside
     a translated value, which is the exact defect `InlineEdit`'s `editSuffix`
     is recorded for — and it also fixes English word order for every locale.
     `{count}` is a plural sub-key for the same reason `boardMap.summary`'s
     two counts are: one sentence, one `count` slot, in this catalogue. */
  const cardCount = t(
    'boardMap.pin.cardCount',
    { count: pin.cards.length },
    pin.cards.length,
  );
  const accessibleName =
    overflow > 0
      ? t('boardMap.pin.nameWithOverflow', {
          location: title,
          count: cardCount,
          names,
          overflow,
          appearance: label,
        })
      : t('boardMap.pin.name', {
          location: title,
          count: cardCount,
          names,
          appearance: label,
        });

  return (
    <button
      type="button"
      aria-label={accessibleName}
      aria-pressed={selected}
      className={cn(
        'absolute flex size-11 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full border-2 border-surface text-sm font-semibold shadow-card outline-offset-2',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
        selected ? 'scale-110 ring-2 ring-accent' : 'hover:scale-105',
        className,
      )}
      style={{
        ...style,
        left: `${position.leftPercent}%`,
        top: `${position.topPercent}%`,
      }}
      onClick={onSelect}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <MapPin aria-hidden="true" className="absolute inset-0 size-full" />
      <span className="relative z-10 text-[0.7rem] leading-none">{pin.cards.length}</span>
    </button>
  );
}

/**
 * FE-80.1 board-map pin-colours. The pin button's appearance phrase uses the
 * translated colour display name and a whole sentence key; the selected-card
 * row below remains on `mapPinAppearanceLabel`, which uses its own row-specific
 * wording while sharing the same fixed colour vocabulary.
 */
function pinStyle(
  t: InterfaceLanguageContextValue['t'],
  pin: BoardMapPin,
): {
  readonly style: CSSProperties;
  readonly className: string;
  readonly label: string;
} {
  const appearance = pin.cards[0]?.pinAppearance;
  if (appearance?.source === 'cover_colour') {
    const swatch = labelSwatch(appearance.colour as LabelColour);
    return {
      style:
        swatch === null
          ? { backgroundColor: 'var(--color-board)' }
          : { backgroundColor: swatch.fill },
      className: swatch?.ink === 'light' ? 'text-board-ink-light' : 'text-board-ink-dark',
      label: t('boardMap.pin.appearance.colourCover', {
        colour: labelColourDisplayName(t, appearance.colour as LabelColour),
      }),
    };
  }
  if (appearance?.source === 'label') {
    const swatch = labelSwatch(appearance.label.colour);
    return {
      style:
        swatch === null
          ? { backgroundColor: 'var(--color-board)' }
          : { backgroundColor: swatch.fill },
      className: swatch?.ink === 'light' ? 'text-board-ink-light' : 'text-board-ink-dark',
      label: t('boardMap.pin.appearance.colourLabel', {
        colour: labelColourDisplayName(t, appearance.label.colour),
      }),
    };
  }
  if (appearance?.source === 'cover_photo') {
    return {
      style: {
        backgroundColor: 'var(--color-board)',
        backgroundImage:
          'linear-gradient(135deg, var(--color-accent), var(--color-board))',
      },
      className: 'text-board-ink-light',
      label: t('boardMap.pin.appearance.photoCover'),
    };
  }
  if (appearance?.source === 'cover_attachment') {
    return {
      style: {
        backgroundColor: 'var(--color-board)',
        backgroundImage:
          'repeating-linear-gradient(135deg, var(--color-panel) 0 8px, var(--color-surface-raised) 8px 16px)',
      },
      className: 'text-ink',
      label: t('boardMap.pin.appearance.attachmentCover'),
    };
  }
  return {
    style: { backgroundColor: 'var(--color-surface-raised)' },
    className: 'text-ink',
    label: t('boardMap.pin.appearance.default'),
  };
}

function coordinatesFromEvent(event: MouseEvent<HTMLElement> | undefined): {
  readonly lat: number;
  readonly lng: number;
} {
  if (event === undefined) return { lat: 0, lng: 0 };
  const rect = event.currentTarget.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return { lat: 0, lng: 0 };
  const x = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
  const y = Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1);
  return {
    lat: normaliseMapCoordinateInput(90 - y * 180, 'lat') ?? 0,
    lng: normaliseMapCoordinateInput(x * 360 - 180, 'lng') ?? 0,
  };
}

function locationFromDraft(draft: CreateDraft): CardLocationPut | null {
  const lat = normaliseMapCoordinateInput(draft.lat, 'lat');
  const lng = normaliseMapCoordinateInput(draft.lng, 'lng');
  if (lat === null || lng === null) return null;
  const text = draft.text.trim();
  return {
    text: text.length === 0 ? null : text,
    lat,
    lng,
  };
}
